import { createContext, useCallback, useContext, useEffect, useState } from 'react';
import { api, getToken, setToken, ApiError } from '../api/client.js';

const AuthContext = createContext(null);

export function AuthProvider({ children }) {
  const [operator, setOperator] = useState(null);
  const [status, setStatus] = useState('checking'); // checking | authed | anonymous

  useEffect(() => {
    let cancelled = false;
    async function restore() {
      const token = getToken();
      if (!token) {
        setStatus('anonymous');
        return;
      }
      try {
        const me = await api.me();
        if (!cancelled) {
          setOperator(me);
          setStatus('authed');
        }
      } catch (err) {
        // Stored token is stale/invalid — fall back to the login screen rather than getting
        // stuck on a spinner, but don't wipe the token on a plain network hiccup (status 0).
        if (!cancelled) {
          if (err instanceof ApiError && err.status !== 0) setToken(null);
          setStatus('anonymous');
        }
      }
    }
    restore();
    return () => {
      cancelled = true;
    };
  }, []);

  const login = useCallback(async (email, password) => {
    const result = await api.login(email, password);
    setToken(result.token);
    setOperator(result.operator);
    setStatus('authed');
    return result.operator;
  }, []);

  const logout = useCallback(() => {
    setToken(null);
    setOperator(null);
    setStatus('anonymous');
  }, []);

  return (
    <AuthContext.Provider value={{ operator, status, login, logout }}>{children}</AuthContext.Provider>
  );
}

export function useAuth() {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuth must be used within AuthProvider');
  return ctx;
}
